import { errorSchema } from "@app/shared";
import type { ZodType } from "zod";
import { ApiError } from "../lib/api";
import { type CorrectionEnvoi, type EnvoiProgramme, envoiProgrammeSchema } from "./contract";
import type { DetailErreur } from "./diffusion-texts";

/**
 * Ce que les onglets de l'ecran « Diffusion » partagent cote requetes : le delai de relecture, la question « la
 * session est-elle perdue ? », et les deux appels d'ecriture. `apiGet` (`lib/api.ts`, au template) ne sait que
 * lire ; les ecritures de la regie sont des `POST`, `PATCH` et `PUT`.
 *
 * **Aucun texte du serveur ne sort de ces fonctions.** Un refus devient une `ApiError` qui porte le code et le
 * statut de l'API (`erreurEcritureText` en fait une phrase de la regie), jamais son message : l'ecran ne repete pas ce
 * que le serveur a ecrit.
 *
 * **Une ecriture a un delai** (`DELAI_ECRITURE_MS`) : une requete qui ne repond pas ne doit pas tenir la file des
 * ecritures d'une semaine, ni un bouton occupe, indefiniment. Passe le delai, la requete est abandonnee
 * (`AbortController`) et l'ecriture leve une `ApiError` de code `delai_depasse`. Abandonner ne dit pas que le serveur
 * n'a rien fait : l'ecran relit ce que l'ecriture a pu changer pour le savoir.
 *
 * **Une issue peut etre incertaine** (`issueIncertaine`) : un delai depasse (`delai_depasse`), un reseau coupe
 * (`coupure`, l'erreur de `fetch`) et une reponse 2xx que l'ecran ne lit pas (`contract_broken`) laissent l'ecriture
 * peut-etre faite. Les trois ont le statut 0 ou 2xx, jamais celui d'un refus ; chaque geste de la regie en fait la
 * meme chose : une phrase « a peut-etre abouti » et une relecture de ce qu'il a pu changer.
 */

/** Le delai entre deux relectures, page ouverte. */
export const DIFFUSION_REFRESH_MS = 30_000;

/** Le delai d'une ecriture : vingt secondes pour obtenir la reponse, corps compris. */
export const DELAI_ECRITURE_MS = 20_000;

/**
 * Le delai du seul POST du brouillon de TikTok : le serveur lit le clip (64 Mo au plus) puis fait un PUT qui peut
 * durer cinq minutes (`DELAI_ENVOI_MS`). Couper plus tot ferait croire a un echec un envoi qui aboutit.
 */
export const DELAI_BROUILLON_MS = 360_000;

/** La cle de l'etat des reseaux : l'onglet « Reseaux » et l'onglet « La semaine » (ses cases) lisent la meme route. */
export const RESEAUX_KEY = ["diffusion", "reseaux"] as const;

/** Vrai pour une erreur qui dit que la session est perdue : la relire ne servirait a rien. */
export function sessionLost(error: Error | null): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

/** Le code et le statut d'une erreur d'ecriture, ou deux `null` pour toute autre erreur (une erreur du code, pas du reseau). */
export function detailDe(erreur: unknown): DetailErreur {
  return erreur instanceof ApiError
    ? { code: erreur.code, status: erreur.status }
    : { code: null, status: null };
}

/**
 * Les codes d'une ecriture dont on ne sait pas si elle a abouti : aucune reponse a temps (`delai_depasse`), le reseau
 * coupe (`coupure`), une reponse 2xx que l'ecran ne lit pas (`contract_broken`, le serveur a repondu oui). Un refus net
 * (400, 409, 5xx) n'en fait pas partie : le serveur a dit non, rien n'a change.
 */
const CODES_INCERTAINS: ReadonlySet<string> = new Set([
  "delai_depasse",
  "coupure",
  "contract_broken",
]);

/** Vrai pour le detail (code et statut) d'une ecriture dont l'issue est incertaine. */
export function issueIncertaineDuDetail(detail: DetailErreur): boolean {
  return detail.code !== null && CODES_INCERTAINS.has(detail.code);
}

/**
 * Vrai pour l'erreur d'une **ecriture** dont l'issue est incertaine : elle a pu aboutir. L'ecran le dit (« a peut-etre
 * abouti ») et relit ce que l'ecriture a pu changer, au lieu de dire « reessaie » comme pour un refus net.
 */
export function issueIncertaine(erreur: unknown): boolean {
  return issueIncertaineDuDetail(detailDe(erreur));
}

/** L'erreur d'une reponse qui n'est pas un succes : le code de l'API s'il a la forme d'une erreur, sinon `http_error`. */
async function refus(response: Response): Promise<ApiError> {
  const payload: unknown = await response.json().catch(() => null);
  const lu = errorSchema.safeParse(payload);
  return new ApiError(
    lu.success ? lu.data.error.code : "http_error",
    "Ecriture refusee",
    response.status,
  );
}

type MethodeEcriture = "POST" | "PATCH" | "PUT" | "DELETE";

/**
 * `fetch`, dont l'echec de reseau (un `TypeError`, la seule erreur que `fetch` leve quand la requete ne va pas au bout)
 * devient une `ApiError` de code `coupure`, statut 0 : aucune reponse. La requete a pu partir avant la coupure. Toute
 * autre erreur, dont l'abandon du delai, passe telle quelle.
 */
async function appeler(chemin: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(chemin, init);
  } catch (erreur) {
    if (erreur instanceof TypeError) {
      throw new ApiError("coupure", "Ecriture coupee", 0);
    }
    throw erreur;
  }
}

/**
 * Fait `travail` avec un signal que le delai abandonne. Le minuteur couvre toute l'ecriture, corps de la reponse
 * compris, et il est toujours lache a la fin. Une ecriture abandonnee leve `delai_depasse` (statut 0 : aucune reponse) ;
 * toute autre erreur passe telle quelle.
 */
async function avecDelai<T>(
  travail: (signal: AbortSignal) => Promise<T>,
  delaiMs: number = DELAI_ECRITURE_MS,
): Promise<T> {
  const controleur = new AbortController();
  const minuteur = setTimeout(() => controleur.abort(), delaiMs);
  try {
    return await travail(controleur.signal);
  } catch (erreur) {
    if (controleur.signal.aborted) {
      throw new ApiError("delai_depasse", "Ecriture sans reponse", 0);
    }
    throw erreur;
  } finally {
    clearTimeout(minuteur);
  }
}

/**
 * Envoie une ecriture et lit sa reponse JSON avec `schema`. Un `corps` est envoye en JSON (avec son `Content-Type`,
 * que les routes qui lisent un corps exigent) ; sans `corps`, rien n'est envoye, comme pour les `POST` de la
 * semaine. Une reponse qui n'est pas celle du contrat leve `contract_broken`.
 */
export function envoyer<T>(
  methode: MethodeEcriture,
  chemin: string,
  corps: unknown,
  schema: ZodType<T>,
  delaiMs: number = DELAI_ECRITURE_MS,
): Promise<T> {
  return avecDelai(async (signal) => {
    const entetes: Record<string, string> = { accept: "application/json" };
    const init: RequestInit = { method: methode, headers: entetes, signal };
    if (corps !== undefined) {
      entetes["content-type"] = "application/json";
      init.body = JSON.stringify(corps);
    }
    const response = await appeler(chemin, init);
    if (!response.ok) {
      throw await refus(response);
    }
    const payload: unknown = await response.json().catch(() => null);
    const lu = schema.safeParse(payload);
    if (!lu.success) {
      throw new ApiError("contract_broken", "Reponse hors contrat", response.status);
    }
    return lu.data;
  }, delaiMs);
}

/** Envoie une ecriture sans corps dont la reponse est vide (204) : « Relire le dossier », « C'est parti ». */
export function envoyerSansReponse(methode: MethodeEcriture, chemin: string): Promise<void> {
  return avecDelai(async (signal) => {
    const response = await appeler(chemin, {
      method: methode,
      headers: { accept: "application/json" },
      signal,
    });
    if (!response.ok) {
      throw await refus(response);
    }
  });
}

/**
 * Envoie des octets bruts (`PUT`, un visuel du planning) et lit la reponse JSON avec `schema`. Le `Content-Type` est celui
 * du fichier ; memes erreurs et meme delai que `envoyer`.
 */
export function envoyerOctets<T>(
  chemin: string,
  octets: Blob,
  schema: ZodType<T>,
  delaiMs: number = DELAI_ECRITURE_MS,
): Promise<T> {
  return avecDelai(async (signal) => {
    const response = await appeler(chemin, {
      method: "PUT",
      headers: {
        accept: "application/json",
        "content-type": octets.type || "application/octet-stream",
      },
      body: octets,
      signal,
    });
    if (!response.ok) {
      throw await refus(response);
    }
    const payload: unknown = await response.json().catch(() => null);
    const lu = schema.safeParse(payload);
    if (!lu.success) {
      throw new ApiError("contract_broken", "Reponse hors contrat", response.status);
    }
    return lu.data;
  }, delaiMs);
}

/** Les codes dont le message est une phrase de la regie, ecrite par la route de correction. */
export const CODES_METIER: ReadonlySet<string> = new Set([
  "demande_refusee",
  "pas_prevu",
  "trop_de_libres",
  "envoi_inconnu",
]);

/**
 * `PATCH /api/diffusion/envois/{id}` : corrige un envoi encore prevu. **Seule ecriture dont le message du serveur
 * sort** : ici le serveur ecrit une phrase de l'app, en francais, pour l'utilisateur (« Le titre ne va que sur
 * YouTube », « l'heure veut 15 minutes d'avance »), et la ligne de l'envoi la montre telle quelle. Le message est
 * garde dans l'`ApiError` **pour les quatre codes metier seulement** (`CODES_METIER` : 400, 404 et 409 que la route ecrit
 * elle-meme) ; tout autre refus (401, 403, 5xx, reponse sans la forme d'une erreur, erreur de forme du validateur) garde le
 * texte generique, et l'ecran le dit par `erreurEcritureText` comme pour les autres ecritures.
 */
export function corrigerEnvoi(
  id: string,
  correction: CorrectionEnvoi,
  delaiMs: number = DELAI_ECRITURE_MS,
): Promise<EnvoiProgramme> {
  return avecDelai(async (signal) => {
    const response = await appeler(`/api/diffusion/envois/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { accept: "application/json", "content-type": "application/json" },
      body: JSON.stringify(correction),
      signal,
    });
    const payload: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const lu = errorSchema.safeParse(payload);
      throw new ApiError(
        lu.success ? lu.data.error.code : "http_error",
        lu.success && CODES_METIER.has(lu.data.error.code) && lu.data.error.message.trim() !== ""
          ? lu.data.error.message
          : "Ecriture refusee",
        response.status,
      );
    }
    const lu = envoiProgrammeSchema.safeParse(payload);
    if (!lu.success) {
      throw new ApiError("contract_broken", "Reponse hors contrat", response.status);
    }
    return lu.data;
  }, delaiMs);
}
